"use client";

import { type DateValue, fromDate, getLocalTimeZone, parseDate } from "@internationalized/date";
import { useEffect, useRef, useState } from "react";
import { DialogTrigger, type Key } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { Button } from "../Button";
import { Calendar } from "../Calendar";
import { Icon } from "../Icon";
import { ListBoxItem } from "../ListBoxItem";
import { Popover } from "../Popover";
import { RangeCalendar } from "../RangeCalendar";
import { SegmentedControl } from "../SegmentedControl";
import { Select } from "../Select";

/**
 * Check if a Date object is valid (not Invalid Date)
 */
function isValidDate(date: Date): boolean {
  return date instanceof Date && !Number.isNaN(date.getTime());
}

/**
 * Safely get a valid date, falling back to current time if invalid
 */
function safeDate(date: Date): Date {
  if (isValidDate(date)) {
    return date;
  }
  console.warn("Invalid date detected in TimeControls, using fallback:", date);
  return new Date();
}

/**
 * Window size for data aggregation
 * Matches GraphQL WindowSize enum
 */
export type WindowSize = "FIFTEEN_MINUTES" | "HOUR" | "DAY" | "WEEK" | "MONTH" | "YEAR";

/**
 * Date range with native Date objects
 */
export interface TimeRange {
  after: Date;
  before: Date;
}

/**
 * Preset range option
 */
export interface PresetRange {
  id: string;
  label: string;
  getRange: () => TimeRange;
}

/**
 * Window size option
 */
export interface WindowSizeOption {
  id: WindowSize;
  label: string;
}

/**
 * Props for TimeControls component
 */
export interface TimeControlsProps {
  /**
   * Current date range
   */
  dateRange: TimeRange;
  /**
   * Callback when date range changes
   */
  onDateRangeChange: (range: TimeRange) => void;
  /**
   * Current window size (optional)
   */
  windowSize?: WindowSize;
  /**
   * Callback when window size changes (optional)
   */
  onWindowSizeChange?: (size: WindowSize) => void;
  /**
   * Whether to show window size selector
   * @default true
   */
  showWindowSize?: boolean;
  /**
   * Auto-adjust window size based on date range
   * @default false
   */
  autoAdjustWindowSize?: boolean;
  /**
   * Whether to show range selector presets
   * @default true
   */
  showRangeSelector?: boolean;
  /**
   * Whether to show calendar date picker button
   * @default false
   */
  allowDatePicker?: boolean;
  /**
   * Whether window size is locked (read-only)
   * @default false
   */
  lockWindowSize?: boolean;
  /**
   * Restrict to single day selection (uses Calendar instead of RangeCalendar)
   * @default false
   */
  restrictToSingleDay?: boolean;
  /**
   * Available preset range options
   */
  presetRanges?: PresetRange[];
  /**
   * Available window size options
   */
  windowSizeOptions?: WindowSizeOption[];
  /**
   * Additional CSS classes
   */
  className?: string;
}

const WINDOW_SIZE_SORT_MAP: Record<WindowSize, number> = {
  FIFTEEN_MINUTES: 1,
  HOUR: 2,
  DAY: 3,
  WEEK: 4,
  MONTH: 5,
  YEAR: 6,
};

// Default window size options (excludes YEAR and MONTH)
const defaultWindowSizeOptions: WindowSizeOption[] = [
  { id: "FIFTEEN_MINUTES", label: "15m" },
  { id: "HOUR", label: "1h" },
  { id: "DAY", label: "1d" },
  { id: "WEEK", label: "1w" },
];

/**
 * TimeControls
 *
 * Sophisticated time control component with responsive behavior and calendar integration.
 * Features preset ranges, custom date picker, and window size selection.
 *
 * **Responsive Behavior:**
 * - < 600px: Compact mode with Select dropdowns
 * - ≥ 600px: Expanded mode with ToggleButton group for presets
 *
 * @example
 * ```tsx
 * const [dateRange, setDateRange] = useState({
 *   after: subDays(new Date(), 7),
 *   before: new Date()
 * });
 * const [windowSize, setWindowSize] = useState<WindowSize>("HOUR");
 *
 * <TimeControls
 *   dateRange={dateRange}
 *   onDateRangeChange={setDateRange}
 *   windowSize={windowSize}
 *   onWindowSizeChange={setWindowSize}
 *   presetRanges={presetRanges}
 *   allowDatePicker
 * />
 * ```
 */
export function TimeControls({
  dateRange,
  onDateRangeChange,
  windowSize,
  onWindowSizeChange,
  showWindowSize = true,
  showRangeSelector = true,
  allowDatePicker = false,
  lockWindowSize = false,
  restrictToSingleDay = false,
  presetRanges = [],
  windowSizeOptions = defaultWindowSizeOptions,
  autoAdjustWindowSize = false,
  className,
}: TimeControlsProps) {
  const [componentWidth, setComponentWidth] = useState(0);
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const [hasManualWindowSize, setHasManualWindowSize] = useState(false);

  // Auto-select appropriate window size based on date range duration
  const getAppropriateWindowSize = (range: TimeRange): WindowSize => {
    const durationMs = range.before.getTime() - range.after.getTime();
    const durationHours = durationMs / (1000 * 60 * 60);

    if (durationHours <= 24) return "FIFTEEN_MINUTES"; // 1 day or less: 15min windows
    if (durationHours <= 72) return "HOUR"; // 3 days or less: 1h windows
    if (durationHours <= 168) return "HOUR"; // 7 days or less: 1h windows
    if (durationHours <= 720) return "DAY"; // 30 days or less: 1d windows
    return "DAY"; // More than 30 days: 1d windows
  };

  // Auto-adjust window size when date range changes (if enabled and not manually overridden)
  useEffect(() => {
    if (autoAdjustWindowSize && !hasManualWindowSize && onWindowSizeChange) {
      const appropriateSize = getAppropriateWindowSize(dateRange);
      onWindowSizeChange(appropriateSize);
    }
  }, [dateRange, autoAdjustWindowSize, hasManualWindowSize, onWindowSizeChange]);

  // Find matching preset range by comparing dates
  const findMatchingPresetRange = (range: TimeRange): string | undefined => {
    return presetRanges.find((presetRange) => {
      const presetDates = presetRange.getRange();
      // Within 24 hours tolerance
      return (
        Math.abs(presetDates.after.getTime() - range.after.getTime()) < 1000 * 60 * 60 * 24 &&
        Math.abs(presetDates.before.getTime() - range.before.getTime()) < 1000 * 60 * 60 * 24
      );
    })?.id;
  };

  const [selectedPresetRange, setSelectedPresetRange] = useState<string | undefined>(() =>
    findMatchingPresetRange(dateRange)
  );

  // Convert JavaScript Date to CalendarDate using local timezone
  // Subtract 1 day from `before` since it's stored as exclusive end (start of next day)
  // Use safeDate to handle potentially invalid dates
  const safeDateAfter = safeDate(dateRange.after);
  const safeDateBefore = safeDate(dateRange.before);
  const endDate = new Date(safeDateBefore);
  endDate.setDate(endDate.getDate() - 1);

  const calendarValue = {
    start: fromDate(safeDateAfter, getLocalTimeZone()),
    end: fromDate(endDate, getLocalTimeZone()),
  };

  // ResizeObserver for responsive behavior
  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setComponentWidth(entry.contentRect.width);
      }
    });

    if (toolbarRef.current) {
      resizeObserver.observe(toolbarRef.current);
    }

    return () => {
      if (toolbarRef.current) {
        resizeObserver.unobserve(toolbarRef.current);
      }
    };
  }, []);

  // Sort window size options
  const sortedWindowSizeOptions = [...windowSizeOptions].sort(
    (a, b) => WINDOW_SIZE_SORT_MAP[a.id] - WINDOW_SIZE_SORT_MAP[b.id]
  );

  const handlePresetChange = (value: string | Key) => {
    const presetId = value.toString();
    const preset = presetRanges.find((p) => p.id === presetId);
    if (preset) {
      setSelectedPresetRange(presetId);
      setHasManualWindowSize(false); // Reset manual override when using preset
      onDateRangeChange(preset.getRange());
    }
  };

  const handleDatePickerChange = (value: { start: DateValue; end: DateValue } | null) => {
    if (value?.start && value?.end) {
      setSelectedPresetRange(undefined);
      // Add +1 day to end date for inclusive end-of-day
      const startDate = new Date(value.start.year, value.start.month - 1, value.start.day, 0, 0, 0, 0);
      const endDate = new Date(value.end.year, value.end.month - 1, value.end.day + 1, 0, 0, 0, 0);
      onDateRangeChange({
        after: startDate,
        before: endDate,
      });
    }
  };

  const handleSingleDateChange = (value: DateValue | null) => {
    if (value) {
      setSelectedPresetRange(undefined);
      const startDate = new Date(value.year, value.month - 1, value.day, 0, 0, 0, 0);
      const endDate = new Date(value.year, value.month - 1, value.day + 1, 0, 0, 0, 0);
      onDateRangeChange({
        after: startDate,
        before: endDate,
      });
    }
  };

  const handleWindowSizeChange = (value: Key) => {
    if (onWindowSizeChange) {
      setHasManualWindowSize(true); // Mark as manually set
      onWindowSizeChange(value.toString() as WindowSize);
    }
  };

  const handleReset = () => {
    if (presetRanges.length > 0) {
      const defaultPreset = presetRanges[0];
      setSelectedPresetRange(defaultPreset.id);
      setHasManualWindowSize(false); // Reset manual override
      onDateRangeChange(defaultPreset.getRange());
    }
  };

  const formatLocalDate = (date: Date, dayDelta = 0) => {
    const validDate = safeDate(date);
    const adjusted = new Date(validDate);
    if (dayDelta !== 0) {
      adjusted.setDate(adjusted.getDate() + dayDelta);
    }
    return adjusted.toLocaleDateString("en-US");
  };

  // Convert options to SelectItem format for proper mobile tray label resolution
  const windowSizeItems = sortedWindowSizeOptions.map((option) => ({
    id: option.id,
    label: option.label,
    value: option.id as string,
  }));

  const presetRangeItems = presetRanges.map((preset) => ({
    id: preset.id,
    label: preset.label,
    value: preset.id,
  }));

  // Window size display (locked or selectable)
  const windowSizeControl = lockWindowSize ? (
    <div className="flex items-center rounded-md border border-border-muted bg-background-muted px-3 py-2 text-sm text-text-muted">
      {sortedWindowSizeOptions.find((opt) => opt.id === windowSize)?.label || windowSize}
    </div>
  ) : (
    <Select
      className="w-full md:w-auto"
      selectedKey={windowSize}
      onSelectionChange={handleWindowSizeChange}
      items={windowSizeItems}
      size="md"
      aria-label="Select Window Size"
    >
      {(item) => (
        <ListBoxItem key={item.id} id={item.id} aria-label={item.label} size="md">
          {item.label}
        </ListBoxItem>
      )}
    </Select>
  );

  // Date picker button (single day or range)
  const datePicker = restrictToSingleDay ? (
    <div className="flex items-center">
      <DialogTrigger>
        <Button
          className="border-l pl-2 pr-2 hover:border-border-default hover:bg-background-muted hover:text-action-primary"
          variant="secondary"
        >
          <Icon name="CalendarBlank" size={18} />
          <span className="ml-2 hidden md:block">{formatLocalDate(dateRange.after)}</span>
        </Button>
        <Popover className="w-auto p-3" disableGroup>
          <Calendar
            aria-label="Select date"
            value={parseDate(safeDateAfter.toISOString().split("T")[0])}
            onChange={handleSingleDateChange}
          />
        </Popover>
      </DialogTrigger>
    </div>
  ) : (
    <div className="flex items-center">
      <DialogTrigger>
        <Button
          className="rounded-r-none border-l pl-2 pr-2 hover:border-border-default hover:bg-background-muted hover:text-action-primary"
          variant="secondary"
        >
          <Icon name="CalendarBlank" size={18} />
          {!selectedPresetRange && (
            <span className="ml-2 hidden md:block">{`${formatLocalDate(dateRange.after)} - ${formatLocalDate(dateRange.before, -1)}`}</span>
          )}
        </Button>
        <Popover className="w-auto p-3" disableGroup>
          <RangeCalendar aria-label="Date range calendar" value={calendarValue} onChange={handleDatePickerChange} />
        </Popover>
      </DialogTrigger>
      {!selectedPresetRange && (
        <Button variant="ghost" size="md" onPress={handleReset}>
          Reset
        </Button>
      )}
    </div>
  );

  // Responsive rendering
  const isCompact = componentWidth < 600;

  return (
    <div ref={toolbarRef} className={twMerge("flex w-full flex-wrap items-center justify-between gap-4", className)}>
      {isCompact ? (
        // Compact mode: Select dropdowns
        <div className="flex w-full justify-between">
          <div className="flex items-center">
            {allowDatePicker && datePicker}
            {showRangeSelector && selectedPresetRange !== undefined && (
              <Select
                selectedKey={selectedPresetRange}
                onSelectionChange={handlePresetChange}
                items={presetRangeItems}
                size="md"
                aria-label="Select Date Range"
              >
                {(item) => (
                  <ListBoxItem key={item.id} id={item.id} size="md">
                    {item.label}
                  </ListBoxItem>
                )}
              </Select>
            )}
          </div>
          <div className="flex items-center">{showWindowSize && windowSize && windowSizeControl}</div>
        </div>
      ) : (
        // Expanded mode: ToggleButton group
        <>
          <div className="flex items-center gap-4">
            {allowDatePicker && datePicker}
            {showRangeSelector && selectedPresetRange !== undefined && (
              <SegmentedControl
                options={presetRanges.map((preset) => ({
                  id: preset.id,
                  label: preset.label,
                }))}
                value={selectedPresetRange}
                onChange={handlePresetChange}
                size="sm"
                variant="secondary"
                aria-label="Select Date Range"
              />
            )}
          </div>
          <div className="flex items-center">{showWindowSize && windowSize && windowSizeControl}</div>
        </>
      )}
    </div>
  );
}
